/**
 * Pure radar-bubble filtering logic (filter-fix-v2).
 *
 * All filter dimensions are present on each RadarBubble payload, so filtering
 * is fully client-side. Bubble GET only takes time_range_days (+ teacher incoming).
 */
import type { RadarBubble } from '@/services/api/teacher'
import type { RadarFilterState, RadarStatusBucket, RadarZone, RadarRay } from '@/services/api/teacher'
import { DEFAULT_RADAR_FILTER, cloneFilterState, ALL_STATUS_BUCKETS, defaultFilterForRadarType } from '../types/filter'
import type { RadarType } from '../types/filter'
import type { RadarFilterSpecials } from '@/services/api/teacher'

export const SPECIALS_KEYS: (keyof RadarFilterSpecials)[] = [
  'prep_open',
  'prep_requested',
  'storno_pending',
  'director_action_pending',
  'has_unread',
  'is_split',
  'partial',
  'needs_admin_assignment',
  // frost-v2: «Beim Direktor» — nur Frost-Bubbles
  'director_gate_pending',
]

// ---------------------------------------------------------------------------
// Status normalization — the bubble.status field may carry raw request values
// or display buckets (see radar_visualization_service STATUS_COLOR_MAP).
// Map every known value onto one of the 7 display buckets.
// ---------------------------------------------------------------------------
const FILLED_STATUSES = new Set(['accepted', 'filled', 'booked', 'assigned', 'accepted_lesson'])
const PENDING_STATUSES = new Set(['pending', 'partially_filled', 'no_substitute_found', 'failed'])

export function bucketForBubble(bubble: RadarBubble): RadarStatusBucket {
  const s = bubble.status
  if (FILLED_STATUSES.has(s)) return 'filled'
  if (s === 'interested') return 'interested'
  if (s === 'declined') return 'declined'
  if (s === 'cancelled') return 'cancelled'
  if (s === 'expired') return 'expired'
  if (s === 'assigned_elsewhere') return 'assigned_elsewhere'
  if (PENDING_STATUSES.has(s)) return 'pending'
  // Unknown values default to pending (visible) rather than disappearing.
  return 'pending'
}

export function zoneForBubble(bubble: RadarBubble): RadarZone {
  if (bubble.is_in_panic_zone) return 'panic'
  if (bubble.is_in_middle_zone) return 'middle'
  return 'outer'
}

function isPartial(bubble: RadarBubble): boolean {
  const eff = bubble.effective_lesson_count
  const orig = bubble.original_lesson_count
  return typeof eff === 'number' && typeof orig === 'number' && eff !== orig
}

/**
 * Extract ATOMIC values for a dimension that may be comma-joined at the bubble
 * level (aggregated bubbles store e.g. subject_name = "Deutsch, Mathe").
 *
 * Returns the UNION of:
 *   - distinct values from `bubble.lessons[]` (the reliable per-lesson source), AND
 *   - the bubble-level field split on "," (covers raw bubbles & data inconsistency).
 * filter-fix-v5.
 */
function atomicFieldValues(bubble: RadarBubble, field: 'subject_name' | 'klass_name'): string[] {
  const set = new Set<string>()
  const add = (raw: string | null | undefined) => {
    if (!raw) return
    for (const part of String(raw).split(',')) {
      const v = part.trim()
      if (v) set.add(v)
    }
  }
  // Primary: per-lesson atomic values.
  if (Array.isArray(bubble.lessons)) {
    for (const lesson of bubble.lessons) {
      add(lesson?.[field] ?? null)
    }
  }
  // Union: bubble-level field (atomic for raw bubbles, comma-joined for aggregated).
  add(bubble[field] ?? null)
  return Array.from(set)
}

/** True if the two arrays share at least one value. */
function intersect(a: string[], b: string[]): boolean {
  if (a.length === 0 || b.length === 0) return false
  const setB = new Set(b)
  for (const x of a) if (setB.has(x)) return true
  return false
}

export interface FilterMatchOptions {
  /** When false, skip incoming/outgoing type (admin: all outgoing, no type UI). */
  applyTypeFilter?: boolean
}

function bubbleHasSpecial(bubble: RadarBubble, key: keyof RadarFilterSpecials): boolean {
  switch (key) {
    case 'prep_open': return !!bubble.prep_open
    case 'prep_requested': return !!bubble.prep_requested
    case 'storno_pending': return !!bubble.cancellation_pending
    case 'director_action_pending': return !!bubble.director_action_pending
    case 'has_unread': return !!bubble.has_unread
    case 'is_split': return !!bubble.is_split
    case 'partial': return isPartial(bubble)
    case 'needs_admin_assignment': return !!bubble.needs_admin_assignment
    case 'director_gate_pending': return !!bubble.gate_pending && bubble.bubble_type !== 'incoming'
    default: return false
  }
}

/**
 * Returns true if a bubble passes the filter state.
 */
export function bubbleMatches(
  bubble: RadarBubble,
  state: RadarFilterState,
  opts?: FilterMatchOptions,
): boolean {
  // 1. Status (activeOnly overrides the status list).
  if (state.activeOnly) {
    const bucket = bucketForBubble(bubble)
    if (bucket !== 'pending' && bucket !== 'interested' && bucket !== 'filled') return false
  } else {
    if (!state.status.includes(bucketForBubble(bubble))) return false
  }

  // 2. Zone
  if (!state.zones.includes(zoneForBubble(bubble))) return false

  // 3. Type (skipped on admin / when incoming UI is hidden)
  if (opts?.applyTypeFilter !== false && bubble.bubble_type && !state.types.includes(bubble.bubble_type as RadarFilterState['types'][number])) {
    return false
  }

  // 4. Whitelists (empty = all).
  // subjects/classes use atomic values (aggregated bubbles comma-join the field) —
  // match if ANY of the bubble's lessons' values is selected. filter-fix-v5.
  if (state.subjects.length > 0 && !intersect(state.subjects, atomicFieldValues(bubble, 'subject_name'))) return false
  if (state.faculties.length > 0 && !state.faculties.includes(bubble.requester_faculty ?? '')) return false
  if (state.classes.length > 0 && !intersect(state.classes, atomicFieldValues(bubble, 'klass_name'))) return false
  if (state.requesters.length > 0 && !state.requesters.includes(bubble.requester_short_code ?? '')) return false

  // 5. Specials: OR. Unchecked keys ignored. None checked = no constraint.
  const specialsOn = SPECIALS_KEYS.filter(k => state.specials[k])
  if (specialsOn.length > 0 && !specialsOn.some(k => bubbleHasSpecial(bubble, k))) return false

  return true
}

export function stripDirectorGate(bubbles: RadarBubble[]): RadarBubble[] {
  return bubbles.map(b => (
    b.gate_pending || b.director_action_pending
      ? { ...b, gate_pending: false, director_action_pending: false }
      : b
  ))
}

export function withoutDirectorSpecials(state: RadarFilterState): RadarFilterState {
  return {
    ...state,
    specials: {
      ...state.specials,
      director_action_pending: false,
      director_gate_pending: false,
    },
  }
}

export function filterBubbles(
  bubbles: RadarBubble[],
  state: RadarFilterState,
  opts?: FilterMatchOptions,
): RadarBubble[] {
  return bubbles.filter(b => bubbleMatches(b, state, opts))
}

/**
 * Merge a (possibly partial) stored/server filter onto the hardcoded default,
 * so missing keys never break the UI.
 */
export function mergeDefault(
  partial: Partial<RadarFilterState> | null | undefined,
  radarType?: RadarType,
): RadarFilterState {
  const base = cloneFilterState(radarType ? defaultFilterForRadarType(radarType) : DEFAULT_RADAR_FILTER)
  if (!partial || typeof partial !== 'object') return base
  for (const k of ['status', 'zones', 'types', 'subjects', 'faculties', 'classes', 'requesters'] as const) {
    if (Array.isArray(partial[k])) {
      // filter-fix-v6: subjects/classes may contain legacy comma-joined strings
      // (pre-v5 stored selections of aggregated bubble fields). Split them into
      // atomic values so they keep matching after the atomic-value fix.
      if (k === 'subjects' || k === 'classes') {
        const set = new Set<string>()
        for (const raw of partial[k] as string[]) {
          for (const part of String(raw ?? '').split(',')) {
            const v = part.trim()
            if (v) set.add(v)
          }
        }
        base[k] = Array.from(set) as never
      } else {
        base[k] = [...partial[k]] as never
      }
    }
  }
  if (partial.specials && typeof partial.specials === 'object') {
    for (const key of SPECIALS_KEYS) {
      if (typeof partial.specials[key] === 'boolean') base.specials[key] = partial.specials[key]
    }
  }
  if (typeof partial.activeOnly === 'boolean') base.activeOnly = partial.activeOnly
  return base
}

/**
 * Counts how many dimensions deviate from a reference (default) state.
 * Used for the filter badge: 0 = no active filtering.
 */
export function countActiveFilters(state: RadarFilterState, reference: RadarFilterState = DEFAULT_RADAR_FILTER): number {
  let n = 0
  const lists: (keyof RadarFilterState)[] = ['status', 'zones', 'types', 'subjects', 'faculties', 'classes', 'requesters']
  for (const k of lists) {
    const a = state[k] as unknown as string[]
    const b = reference[k] as unknown as string[]
    if (a.length !== b.length) { n++; continue }
    const setB = new Set(b)
    if (a.some(x => !setB.has(x))) n++
  }
  for (const key of SPECIALS_KEYS) {
    if (state.specials[key] !== reference.specials[key]) n++
  }
  if (state.activeOnly !== reference.activeOnly) n++
  return n
}

/** True when every dimension is at its default (no filtering active). */
export function isDefaultFilter(state: RadarFilterState): boolean {
  return countActiveFilters(state) === 0
}

/**
 * Unique, sorted option values for a whitelist dimension derived from bubbles.
 * For subject_name/klass_name the values come from `lessons[]` ∪ bubble-level
 * (aggregated bubbles comma-join the field). filter-fix-v5.
 */
export function deriveOptions(
  bubbles: RadarBubble[],
  field: 'subject_name' | 'requester_faculty' | 'klass_name' | 'requester_short_code',
): string[] {
  const set = new Set<string>()
  for (const b of bubbles) {
    if (field === 'subject_name' || field === 'klass_name') {
      for (const v of atomicFieldValues(b, field)) set.add(v)
    } else {
      const v = b[field]
      if (v) set.add(v)
    }
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b, 'de'))
}

/** Like deriveOptions, but also counts how many bubbles contain each value. filter-fix-v5. */
export function deriveOptionsWithCounts(
  bubbles: RadarBubble[],
  field: 'subject_name' | 'requester_faculty' | 'klass_name' | 'requester_short_code',
): { value: string; count: number }[] {
  const map = new Map<string, number>()
  for (const b of bubbles) {
    const vals =
      field === 'subject_name' || field === 'klass_name'
        ? atomicFieldValues(b, field)
        : b[field]
          ? [b[field] as string]
          : []
    for (const v of vals) map.set(v, (map.get(v) ?? 0) + 1)
  }
  return Array.from(map.entries())
    .map(([value, count]) => ({ value, count }))
    .sort((a, b) => a.value.localeCompare(b.value, 'de'))
}

/**
 * Filter circumference rays (spokes + labels) so that a ray is only shown when at
 * least one VISIBLE (already filtered) bubble maps to it. Without this, filtering
 * out bubbles leaves "ghost" date/teacher labels on the radar circumference.
 *
 * Linkage (exact string match): date-ray.id ↔ bubble.lesson_date;
 * teacher-ray.id ↔ bubble.requester_teacher_id.
 *
 * filter-fix-v8.
 */
export function filterRays(rays: RadarRay[], filteredBubbles: RadarBubble[]): RadarRay[] {
  if (!rays.length) return []
  if (!filteredBubbles.length) return []

  // Build ray-key -> visible-bubble-count map in one pass. A bubble can map to a
  // date ray (lesson_date) and/or a teacher ray (requester_teacher_id); record both.
  const counts = new Map<string, number>()
  for (const b of filteredBubbles) {
    if (b.lesson_date) counts.set(b.lesson_date, (counts.get(b.lesson_date) ?? 0) + 1)
    if (b.requester_teacher_id) counts.set(b.requester_teacher_id, (counts.get(b.requester_teacher_id) ?? 0) + 1)
  }

  const result: RadarRay[] = []
  for (const ray of rays) {
    if (ray.type === 'date' || ray.type === 'teacher') {
      const c = counts.get(ray.id)
      if (c === undefined) continue // no visible bubble on this ray -> ghost, remove
      result.push({ ...ray, bubble_count: c })
    } else {
      // Unknown/missing type: keep as-is (per-ray fail-open).
      result.push(ray)
    }
  }

  // Global fail-open: if linkage matched nothing although there are visible bubbles
  // AND rays exist, return all rays unchanged (defends against format mismatch that
  // would otherwise hide the entire circumference).
  if (result.length === 0 && rays.length > 0 && filteredBubbles.length > 0) {
    return rays
  }
  return result
}

export { ALL_STATUS_BUCKETS }
